import { useRef, useState, useEffect, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Send, MessageSquare } from "lucide-react";

export interface NoteReply {
  id: string;
  note_id: string;
  author_type: "patient" | "therapist";
  author_id: string;
  body: string;
  created_at: string;
}

interface NoteRepliesSectionProps {
  replies: NoteReply[];
  loading: boolean;
  submitting: boolean;
  onSubmit: (body: string) => Promise<void>;
  currentAuthorType: "patient" | "therapist";
  therapistLabel?: string;
  patientLabel?: string;
  /** "bottom" = stacked below note (max-h constrained). "side" = fills right panel (flex-1). */
  variant?: "bottom" | "side";
}

export default function NoteRepliesSection({
  replies,
  loading,
  submitting,
  onSubmit,
  currentAuthorType,
  therapistLabel,
  patientLabel,
  variant = "bottom",
}: NoteRepliesSectionProps) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [replies.length]);

  const handleSubmit = async () => {
    const body = draft.trim();
    if (!body || submitting) return;
    setDraft("");
    await onSubmit(body);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getAuthorLabel = (reply: NoteReply) => {
    if (reply.author_type === "therapist") return therapistLabel ?? t("notes.replies.therapist");
    return patientLabel ?? t("notes.replies.patient");
  };

  const isMe = (reply: NoteReply) => reply.author_type === currentAuthorType;

  const isSide = variant === "side";

  return (
    <div
      className={`flex flex-col ${
        isSide
          ? "flex-1 min-h-0 border-l border-gray-100 dark:border-white/10"
          : "border-t border-gray-100 dark:border-white/10"
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 shrink-0 border-b border-gray-100 dark:border-white/10">
        <MessageSquare className="w-3.5 h-3.5 text-content-tertiary" />
        <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wide">
          {t("notes.replies.title")}
          {replies.length > 0 && (
            <span className="ml-1.5 font-normal normal-case text-content-tertiary">({replies.length})</span>
          )}
        </span>
      </div>

      {/* Thread */}
      <div
        className={`overflow-y-auto px-4 pb-2 pt-2 flex flex-col gap-2 ${
          isSide ? "flex-1 min-h-0" : "max-h-56"
        }`}
      >
        {loading ? (
          <p className="text-xs text-content-tertiary py-2">{t("common.loading")}</p>
        ) : replies.length === 0 ? (
          <p className="text-xs text-content-tertiary py-2">{t("notes.replies.empty")}</p>
        ) : (
          replies.map((reply) => {
            const mine = isMe(reply);
            return (
              <div
                key={reply.id}
                className={`flex flex-col gap-0.5 ${mine ? "items-end" : "items-start"}`}
              >
                <span className="text-xs text-content-tertiary px-1">
                  {getAuthorLabel(reply)}
                </span>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    mine
                      ? "bg-brand-500 text-white rounded-tr-sm"
                      : "bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-gray-200 rounded-tl-sm"
                  }`}
                >
                  {reply.body}
                </div>
                <span className="text-xs text-gray-300 dark:text-gray-600 px-1">
                  {new Date(reply.created_at).toLocaleTimeString(i18n.language, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex items-end gap-2 px-4 pb-3 pt-1 shrink-0">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("notes.replies.placeholder")}
          rows={1}
          className="field-base field-default flex-1 resize-none bg-surface-sunken px-3 py-2"
          style={{ maxHeight: "80px" }}
          disabled={submitting}
        />
        <button
          onClick={handleSubmit}
          disabled={!draft.trim() || submitting}
          className="shrink-0 p-2 rounded-xl bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
